/**
 * External Imports
 */
import {
  FC,
  MouseEvent,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef
} from 'react';
import { connect, useDispatch } from 'react-redux';
import { format } from 'date-fns';

/**
 * Nexus Imports
 */
import { NeuButton, NeuRadio, NeuAvatar } from '@neutron/react';
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import { BlueChip } from '../../shared/chips/blueChip';
import { Dropdown } from '../../shared/dropdowns';
import Loading from '../../shared/Loading';
import SearchView from '../reusableModalPieces/searchView';
import StopSubHeader from '../reusableModalPieces/subHeader';

import { RootState } from '../../../redux/store';
import {
  openModal,
  setModalType,
  setModalAction,
  setModalData,
  closeModal,
  postNewStoplight,
  updateStoplight,
  deleteStoplight,
  postCommentToStoplight,
  clearStoplight,
  setCurrentStoplightPage,
  addStoplightToSharingQ,
  clearTaskModalState,
  getCategoriesByType,
  saveTaskModalState,
  setToastType,
  ISaveTaskModalStatePayload
} from '../../../redux/actions';
import {
  useLazyGetEmployeesByIdsQuery,
  useLazyGetEmployeesBySearchQuery
} from '../../../redux/apis/Employees.api';

import { handleModal } from '../../../services/ModalPortal/utils';
import useToast from '../../../services/Toast';
import { logAdHocClick, logTaskClick } from '../../../utils/analyticsHelpers';
import { useDebounceValue } from '../../../utils/debouncers';
import {
  date,
  toTitleCase,
  text,
  compareObjByStringKey
} from '../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  Stoplight,
  IFacility,
  Unit,
  Employee,
  Category,
  Issue,
  TaskPost,
  ITempTaskWithNewStoplight
} from '../../../config/interfaces';

/**
 * Style Imports
 */
import { StyledBoldLabel } from '../../shared/table/Table.styles';
import {
  BlockedIcon,
  CardHeader,
  CardMainHeader,
  ChipWrapper,
  CompletedIcon,
  InProgressIcon,
  InputHeading,
  LabelContentBold,
  ModalCardContent,
  ModalTextArea,
  ModalFooter,
  RadioContainer,
  RadioLabel,
  RadioLabelContainer,
  StoplightCard,
  StyledEditHeaderCol,
  StyledRightAlignButton,
  StyledEditHeaderRow,
  StyledBoldHeading,
  StyledCircleDoneIcon,
  TitleSpan,
  TitleIcon,
  ViewIssuesClickableLabel,
  StyledDivider
} from './StoplightModal.styles';

interface IStoplightModalProps {
  authorizedUser: AuthUserProps;
  categories: Category[];
  facilities: IFacility[];
  globalUserSection: string;
  loading?: boolean;
  modalAction: string;
  modalData: Stoplight;
  stoplight: Stoplight | null;
  tempModalState?: ISaveTaskModalStatePayload;
  tempTaskObject?: ITempTaskWithNewStoplight;
  units: Unit[];
}

type RequiredItemTypes = {
  [key: string]: string | [] | {};
};
const requiredItems: RequiredItemTypes = {
  status: '',
  category: '',
  details: ''
};

const StoplightModal: FC<IStoplightModalProps> = ({
  authorizedUser,
  categories,
  facilities,
  globalUserSection,
  loading,
  modalAction,
  modalData,
  stoplight,
  tempModalState,
  tempTaskObject,
  units
}) => {
  const dispatch = useDispatch();

  const [userSection, setUserSection] = useState(globalUserSection);
  const [subCategories, setSubCategories] = useState<string[]>([]);

  const [attachIssueDisplay, setAttachIssueDisplay] = useState<Issue[]>([]);
  const [shareObject, setShareObject] = useState<{
    userName: string;
    email: string[];
  }>({ userName: '', email: [] });

  const [commentObj, setCommentObj] = useState<{
    id: string;
    comment: { body: string; authorId: string; authorFullName: string };
  }>({
    id: '',
    comment: { body: '', authorId: '', authorFullName: '' }
  });
  const [requiredFields, setRequiredFields] = useState(requiredItems);
  const [showButton, setShowButton] = useState<boolean>(false);
  const [view, setView] = useState<'info' | 'search'>('info');
  const [activeSearch, setActiveSearch] = useState<string>('');
  // TODO: These need the is prefix
  const [openCat, setOpenCat] = useState(false);
  const [openSubcat, setOpenSubcat] = useState(false);
  const [openUnit, setOpenUnit] = useState(false);
  const [sharedPopState, setSharedPopState] = useState<boolean>(false);
  const [sharedSearchTerm, setSharedSearchTerm] = useState('');
  const [sharedWithNoEmail, setSharedWithNoEmail] = useState<boolean>(false);

  const modalContentRef = useRef<HTMLNeuCardElement>(null);

  // debouncing search terms
  const dbSharedSearchTerm = useDebounceValue(sharedSearchTerm, 250);

  const { toast } = useToast();

  const { logTrackingEvent } = useAnalyticsApi();

  const [
    getEmployeesByIds,
    {
      data: sharedEmployeesData,
      // isError: isGetEmployeesByIdsError,
      isFetching: isGetEmployeesByIdsFetching
      // isLoading: isGetEmployeesByIdsLoading
    }
  ] = useLazyGetEmployeesByIdsQuery();
  const [
    getEmployeesBySearch,
    {
      data: employeesBySearchData
      // isError: isGetEmployeesBySearchError,
      // isFetching: isGetEmployeesBySearchFetching,
      // isLoading: isGetEmployeesBySearchLoading
    }
  ] = useLazyGetEmployeesBySearchQuery();

  const userFacility = useMemo(
    () =>
      facilities.filter(
        facility => facility.facilityId === authorizedUser.facilityId
      )[0],
    [facilities, authorizedUser.facilityId]
  );

  const [stoplightObject, setStoplightObject] = useState<Stoplight>({
    locations: {
      division: [
        { id: userFacility.division, displayName: userFacility.division }
      ],
      // TODO: Should this be adding coid now not mnemonic
      facility: [
        { id: userFacility.facilityId, displayName: userFacility.value }
      ],
      department:
        (userSection === 'employee' ||
          userSection === 'csc' ||
          userSection === 'cned') &&
        authorizedUser.departmentId
          ? [
              {
                id: authorizedUser.departmentId,
                displayName: authorizedUser.dept
              }
            ]
          : [],
      unit: userSection === 'patient' ? [{ id: '', displayName: '' }] : []
    },
    createdBy: `${authorizedUser.firstName} ${authorizedUser.lastName}`,
    authorId: authorizedUser.hcaid,
    status: 'In Progress',
    issueIds: [],
    created: date.now(),
    origin: tempTaskObject?.roundingType || userSection,
    category: '',
    subCategory: '',
    details: '',
    sharedWith: []
  });

  const handleChangeIssueOrigin = (newOrigin: string) => {
    setStoplightObject({
      ...stoplightObject,
      origin: newOrigin,
      locations: {
        division: [
          { id: userFacility.division, displayName: userFacility.division }
        ],
        facility: [
          { id: userFacility.facilityId, displayName: userFacility.value }
        ],
        department:
          newOrigin === 'employee' && authorizedUser.departmentId
            ? [
                {
                  id: authorizedUser.departmentId,
                  displayName: authorizedUser.dept
                }
              ]
            : [],
        unit: newOrigin === 'patient' ? [{ id: '', displayName: '' }] : []
      }
    });
    setUserSection(newOrigin);
  };

  const handleClose = () => {
    dispatch(clearTaskModalState());
    if (!tempTaskObject?.taskId) {
      handleModal(modalContentRef);
    } else {
      dispatch(closeModal());
    }
    if (
      tempTaskObject &&
      tempTaskObject.taskId &&
      tempModalState &&
      tempModalState.modal
    ) {
      // TODO: This seems duplicated
      dispatch(setModalAction(tempModalState.modalAction ?? ''));
      dispatch(setModalType(tempModalState.modalType ?? ''));
      dispatch(setModalData(tempTaskObject));
      dispatch(
        saveTaskModalState({
          redirect: true
        })
      );
      dispatch(openModal());
    }
  };

  const handleCommentText = (e: CustomEvent<any>) => {
    const target = e.target as HTMLInputElement;
    const value = target.value as string;
    if (stoplightObject?.id)
      setCommentObj({
        id: stoplightObject.id,
        comment: {
          body: value,
          authorId: authorizedUser.hcaid,
          authorFullName: `${authorizedUser.firstName} ${authorizedUser.lastName}`
        }
      });
  };

  const handleCommentPost = useCallback(() => {
    if (commentObj) {
      dispatch(postCommentToStoplight(commentObj));
      setCommentObj({
        id: '',
        comment: { body: '', authorId: '', authorFullName: '' }
      });
      dispatch(setCurrentStoplightPage(1));
    }
  }, [commentObj]);

  const handleDelete = () => {
    dispatch(setToastType('Delete Stoplight'));
    toast(
      stoplightObject.issues && stoplightObject.issues.length > 0
        ? `The stoplight you are about to delete has ${
            stoplightObject.issues?.length
          } ${
            stoplightObject.issues?.length === 1 ? 'issue' : 'issues'
          } attached!`
        : 'Are you sure you want to delete this stoplight?',
      'info',
      null,
      500,
      true,
      stoplightObject.issues && stoplightObject.issues.length > 0
        ? `This won't delete the issues, but they will no longer be attached to the stoplight.`
        : '',
      () => {
        if (stoplight?.id) dispatch(deleteStoplight(stoplight.id));
        handleClose();
      }
    );
  };

  const handleDropdownChange = useCallback(
    // TODO: Need to handle union of string and Unit or create a new method
    (value: any, name: string) => {
      if (name === 'unit') {
        setStoplightObject({
          ...stoplightObject,
          locations: {
            ...stoplightObject.locations,
            unit: [{ id: value.unitId, displayName: value.unit }]
          }
        });
      } else if (name === 'category') {
        setStoplightObject({
          ...stoplightObject,
          [name]: value,
          subCategory: ''
        });
      } else {
        setStoplightObject({ ...stoplightObject, [name]: value });
      }
      // For validation
      if (Object.keys(requiredFields).includes(name)) {
        setRequiredFields({ ...requiredFields, [name]: value });
      }
    },
    [requiredFields, stoplightObject]
  );

  const handleInputs = (e: CustomEvent<any>) => {
    const target = e.target as HTMLInputElement;
    const { name } = target;
    const value = target.value as string;
    if (name === 'unit') {
      setStoplightObject({
        ...stoplightObject,
        locations: {
          ...stoplightObject.locations,
          unit: [{ id: value, displayName: '' }]
        }
      });
    } else {
      setStoplightObject({ ...stoplightObject, [name]: value });
    }
    // For validation
    if (Object.keys(requiredFields).includes(name)) {
      setRequiredFields({ ...requiredFields, [name]: value });
    }
  };

  const handlePost = () => {
    if (modalAction === 'UNIVERSAL-ADD') {
      logTrackingEvent(logAdHocClick('Add Stoplight', 'save'));
    } else {
      logTrackingEvent(logTaskClick('stoplight', 'save'));
    }
    if (stoplightObject.status === 'Completed') {
      dispatch(setToastType('Complete'));
      toast(
        stoplightObject.issues && stoplightObject.issues.length > 0
          ? `The stoplight you are about to complete has ${stoplightObject.issues.length} attached.`
          : 'Are you sure you would like to complete this stoplight?',
        'info',
        null,
        400,
        true,
        stoplightObject.issues && stoplightObject.issues.length > 0
          ? `Completing this stoplight will result in all attached issues being closed.`
          : null,
        () => {
          if (modalAction === 'EDIT' || modalAction === 'VIEW') {
            dispatch(addStoplightToSharingQ(shareObject));
            dispatch(updateStoplight(stoplightObject));
          } else if (showButton) {
            dispatch(addStoplightToSharingQ(shareObject));
            dispatch(postNewStoplight(stoplightObject));
          }
          setTimeout(() => {
            handleClose();
          }, 500);
        }
      );
    } else {
      if (modalAction === 'EDIT' || modalAction === 'VIEW') {
        dispatch(addStoplightToSharingQ(shareObject));
        dispatch(updateStoplight(stoplightObject));
      } else if (showButton) {
        dispatch(addStoplightToSharingQ(shareObject));
        dispatch(postNewStoplight(stoplightObject));
      }
      setTimeout(() => {
        handleClose();
      }, 500);
    }
  };

  const handleRadioChange = (e: CustomEvent<any>) => {
    const target = e.target as HTMLInputElement;
    const { name } = target;
    const { value } = e.detail;

    setStoplightObject({ ...stoplightObject, [name]: value });
    // For validation
    if (Object.keys(requiredFields).includes(name)) {
      setRequiredFields({ ...requiredFields, [name]: value });
    }
  };

  // removing issues from stoplight
  const handleRemoveAttachIssue = useCallback(
    (event: MouseEvent<HTMLNeuIconElement>, iss: Issue) => {
      event.stopPropagation();
      const removedIssue = iss;
      // Update issues in stoplight object
      const remainingIssues = stoplightObject.issueIds.filter(
        (item: string) => {
          return item !== removedIssue.id;
        }
      );
      dispatch(setToastType('Delete Issue'));
      if (stoplight?.issueIds?.includes(iss.id)) {
        toast(
          `Are you sure you would like to un-attach issue ${removedIssue.friendlyId} ?`,
          'info',
          null,
          400,
          true,
          null,
          () => {
            setStoplightObject({
              ...stoplightObject,
              issueIds: remainingIssues
            });
            setAttachIssueDisplay(
              attachIssueDisplay.filter(
                (issue: Issue) => issue.id !== removedIssue.id
              )
            );
          }
        );
      } else {
        setStoplightObject({
          ...stoplightObject,
          issueIds: remainingIssues
        });
        setAttachIssueDisplay(
          attachIssueDisplay.filter(
            (issue: Issue) => issue.id !== removedIssue.id
          )
        );
      }
    },
    [attachIssueDisplay, stoplight, stoplightObject]
  );

  const handleRemoveSharedWith = (emp: string) => {
    const removedEmpName = emp.split(' ').slice(0, 2).join('.');
    const removedEmpId: string = emp?.split(/[\s+]+/).pop() || '';
    // Update sharedWith in task object
    const newSharedWith = stoplightObject.sharedWith.filter((item: string) => {
      return item.includes(removedEmpId.toLowerCase()) === false;
    });
    setStoplightObject({
      ...stoplightObject,
      sharedWith: newSharedWith
    });

    // update Emails for sharing
    const emailsAfterRemove: string[] = shareObject.email.filter(
      (email: string) => {
        const existingString = email.split('@')[0];
        return existingString !== removedEmpName;
      }
    );
    setShareObject({ ...shareObject, email: emailsAfterRemove });
  };

  const handleSharedSearching = (e: CustomEvent<any>) => {
    const target = e.target as HTMLInputElement;
    setSharedSearchTerm(target.value);
  };

  const setEmployeeToShare = (emp: Employee) => {
    const empId = emp.employeeId.toUpperCase();
    const empExists = stoplightObject.sharedWith.includes(empId.toLowerCase());
    if (!empExists) {
      setStoplightObject({
        ...stoplightObject,
        sharedWith: stoplightObject.sharedWith
          ? [...stoplightObject.sharedWith, `${emp.employeeId}`]
          : [`${emp.employeeId}`]
      });
      setShareObject({
        email: [...shareObject.email, emp.email],
        userName: `${authorizedUser.firstName} ${authorizedUser.lastName}`
      });
    } else {
      // TODO future development add a toast here saying 'user already selected'
      console.log('User already selected');
    }
  };

  // adding issues to stoplight
  const setIssuesToAttach = useCallback(
    (issue: Issue) => {
      const issueExists = attachIssueDisplay.some(
        (item: Issue) => item.id === issue.id
      );
      // In order to check whether the immediately removed one can be added again
      const issueExistsInTaskObj = stoplightObject.issueIds?.includes(issue.id);
      if (!issueExists && (!issue.isAttached || issueExistsInTaskObj)) {
        setStoplightObject({
          ...stoplightObject,
          issueIds: stoplightObject.issueIds
            ? [...stoplightObject.issueIds, `${issue.id}`]
            : [issue.id]
        });
        setAttachIssueDisplay([
          ...attachIssueDisplay,
          {
            ...issue,
            taskSubtype: issue.taskSubtype || issue.historicCategory || ''
          }
        ]);
      }
    },
    [attachIssueDisplay, stoplightObject]
  );

  const setRequiredFieldValues = (stopObj: Stoplight) => {
    const { category, status, details, subCategory } = stopObj;
    const { unit } = stopObj.locations;
    if (stoplightObject.origin === 'patient') {
      if (subCategories.length > 0) {
        setRequiredFields({
          unit: unit[0] ? unit[0].id : [],
          category: category || tempTaskObject?.category || '',
          status,
          details,
          subCategory: subCategory || tempTaskObject?.subCategory || ''
        });
      } else {
        setRequiredFields({
          unit: unit[0] ? unit[0].id : [],
          category: category || tempTaskObject?.category || '',
          status,
          details
        });
      }
    } else if (subCategories.length > 0) {
      setRequiredFields({
        category: category || tempTaskObject?.category || '',
        status,
        details,
        subCategory: subCategory || tempTaskObject?.subCategory || ''
      });
    } else {
      setRequiredFields({
        category: category || tempTaskObject?.category || '',
        status,
        details
      });
    }
    setStoplightObject({
      ...stoplightObject,
      locations: {
        // TODO: Need to ensure displayNames are added before saving
        ...stoplightObject.locations,
        unit: unit[0]
          ? [{ id: unit[0].id, displayName: unit[0].displayName }]
          : []
      },
      category: category || tempTaskObject?.category || '',
      subCategory: subCategory || tempTaskObject?.subCategory || ''
    });
  };

  const isAnimated = useMemo(
    () => (!tempModalState?.redirect ? 'show-modal' : ''),
    [tempModalState]
  );

  const filteredUnits = useMemo(
    () => units.filter(unit => unit.unit !== 'ADMIN'),
    [units]
  );

  const sharedWithDisplay = useMemo(
    () =>
      sharedEmployeesData?.map((emp: Employee) => {
        const empId = emp.employeeId?.toUpperCase();
        return `${emp.firstName} ${emp.lastName} ${empId}`;
      }) ?? [],
    [sharedEmployeesData]
  );

  useEffect(() => {
    const tempObject = {
      origin: text.capFirst(userSection),
      status: 'In Progress'
    };
    setStoplightObject({ ...stoplightObject, ...tempObject });
    if (userSection === 'patient') {
      setRequiredFields({
        ...requiredFields,
        ...tempObject,
        unit: ''
      });
    } else {
      setRequiredFields({ ...requiredFields, ...tempObject });
    }
    return () => {
      dispatch(clearStoplight());
    };
  }, []);

  /** Shared with - search */
  useEffect(() => {
    getEmployeesBySearch(dbSharedSearchTerm);
  }, [dbSharedSearchTerm]);

  useEffect(() => {
    const selectedCat: Category = categories.filter((category: Category) => {
      return category.name === stoplightObject.category;
    })[0];
    if (selectedCat) setSubCategories(selectedCat.subCategories);
    else setSubCategories([]);
  }, [stoplightObject.category]);

  useEffect(() => {
    if (stoplightObject?.sharedWith && stoplightObject.sharedWith.length > 0) {
      getEmployeesByIds([...stoplightObject.sharedWith]);
    }
  }, [stoplightObject.sharedWith]);

  // watching for origin change
  useEffect(() => {
    if (userSection === 'patient' || userSection === 'employee') {
      const categoryData = {
        categoryFor: userSection,
        categoryType: 'stoplight' // TODO to test change to issues
      };
      dispatch(getCategoriesByType(categoryData));
    }
    setRequiredFieldValues(stoplightObject);
  }, [userSection]);

  /**
   * Validate fields on change and disable button
   */
  useEffect(() => {
    let count = 0;
    Object.keys(requiredFields).forEach(item => {
      const tempVar = requiredFields[item];
      if (tempVar === '' || (Array.isArray(tempVar) && tempVar.length === 0)) {
        count += 1;
      }
      if (modalAction === 'VIEW') {
        setShowButton(true);
      } else if (count === 0) {
        setShowButton(true);
      } else {
        setShowButton(false);
      }
    });
  }, [requiredFields]);

  useEffect(() => {
    setRequiredFieldValues(stoplightObject);
  }, [subCategories]);

  useEffect(() => {
    if (
      (modalAction === 'EDIT' || modalAction === 'VIEW') &&
      stoplight &&
      Object.keys(stoplight).length > 0
    ) {
      /**
       * In order to add sharedWith property to the feedback object if its not present in the
       * selected feedback for edit/partial edit.
       */
      const tempStoplight = stoplight.sharedWith
        ? {
            ...stoplight,
            details: stoplightObject.details || stoplight.details
          }
        : { ...stoplight, sharedWith: [] };
      setStoplightObject(tempStoplight);

      if (stoplight.issues && stoplight.issues.length > 0) {
        const displayIssues = stoplight.issues.map(issue => ({
          ...issue,
          id: issue.taskId
        }));
        setAttachIssueDisplay(displayIssues);
      }

      /**
       * Validation block
       */
      const { status = '', category = '', details = '' } = stoplight;
      const { unit = [] } = stoplight.locations;
      if (stoplightObject.origin === 'patient') {
        setRequiredFields({
          ...requiredFields,
          status,
          unit: unit[0]?.id,
          category,
          details: stoplightObject.details || details
        });
      } else {
        setRequiredFields({
          ...requiredFields,
          status,
          category,
          details: stoplightObject.details || details
        });
      }
    }
  }, [stoplight]);

  return (
    <StoplightCard
      className={isAnimated}
      id="Stoplight-Modal"
      unit="card"
      ref={modalContentRef}
    >
      <CardHeader className="neu-bg-plain-0" unit="header">
        <CardMainHeader>
          <TitleSpan id="Stoplight-Modal-Title">
            <TitleIcon size="3x" color="blue">
              traffic
            </TitleIcon>
            {modalAction === 'UNIVERSAL-ADD' ? 'New Stoplight' : ''}
            {modalAction === 'VIEW' || modalAction === 'EDIT'
              ? `${toTitleCase(stoplightObject.origin)} | Stoplight`
              : ''}
            {modalAction !== 'EDIT' &&
            modalAction !== 'UNIVERSAL-ADD' &&
            modalAction !== 'VIEW'
              ? `New ${toTitleCase(stoplightObject.origin)} Stoplight`
              : ''}
          </TitleSpan>
          <NeuButton
            data-testid="stoplight-modal-close-button"
            aria-label="close panel"
            color="blue"
            icon="close"
            kind="tertiary"
            size="small"
            onClick={() => {
              if (modalAction === 'UNIVERSAL-ADD') {
                logTrackingEvent(logAdHocClick('Add Stoplight', 'close'));
              } else {
                logTrackingEvent(logTaskClick('stoplight', 'close'));
              }
              handleClose();
            }}
          >
            Close
          </NeuButton>
        </CardMainHeader>
        <StopSubHeader
          completed={stoplight?.status === 'Completed'}
          items={stoplightObject.issueIds}
          type="stoplight"
          view={view}
          setView={setView}
        />
      </CardHeader>

      {(modalAction !== 'EDIT' ||
        (view === 'info' && modalAction === 'EDIT')) &&
        view !== 'search' &&
        (loading || isGetEmployeesByIdsFetching ? (
          <ModalCardContent id="Stoplight-Modal-Loading-Card" unit="content">
            <Loading label="stoplight" />
          </ModalCardContent>
        ) : (
          <ModalCardContent
            className="mb-4"
            id="Stoplight-Modal-Card"
            unit="content"
          >
            {modalAction === 'UNIVERSAL-ADD' &&
              !(stoplightObject.origin && tempTaskObject?.roundingType) && (
                <NeuRadio
                  data-testid="stoplight-modal-origin-radio-group"
                  className="mt-1"
                  unit="group"
                  label="Stoplight Origin"
                  name="origin"
                  required
                  value={stoplightObject.origin ? stoplightObject.origin : ''}
                  onNeuChange={e => handleChangeIssueOrigin(e.detail.value)}
                >
                  <NeuRadio
                    data-testid="stoplight-modal-origin-radio-item-patient"
                    unit="item"
                    label="Patient"
                    name="origin"
                    value="patient"
                  />
                  <NeuRadio
                    data-testid="stoplight-modal-origin-radio-item-employee"
                    unit="item"
                    label="Employee"
                    name="origin"
                    value="employee"
                  />
                </NeuRadio>
              )}

            {(modalAction === 'EDIT' || modalAction === 'VIEW') && (
              <>
                <InputHeading>Stoplight ID</InputHeading>
                <LabelContentBold>
                  {stoplightObject?.friendlyId ?? ''}
                </LabelContentBold>
                <InputHeading>Creator</InputHeading>
                <LabelContentBold className="my-1">
                  {modalAction === 'VIEW'
                    ? stoplightObject?.createdBy ?? ''
                    : ''}
                </LabelContentBold>
              </>
            )}

            <RadioContainer>
              {stoplight?.status !== 'Completed' && (
                <RadioLabelContainer>
                  <RadioLabel>
                    <CompletedIcon>traffic</CompletedIcon>
                  </RadioLabel>
                  <RadioLabel>
                    <InProgressIcon>traffic</InProgressIcon>
                  </RadioLabel>
                  <RadioLabel>
                    <BlockedIcon>traffic</BlockedIcon>
                  </RadioLabel>
                </RadioLabelContainer>
              )}
              <NeuRadio
                data-testid="stoplight-modal-status-radio-group"
                className="mt-1 position-absolute"
                unit="group"
                label="Status"
                name="status"
                required
                value={stoplightObject.status ? stoplightObject.status : ''}
                onNeuChange={handleRadioChange}
              >
                {stoplight?.status !== 'Completed' ? (
                  <>
                    <NeuRadio
                      data-testid="stoplight-modal-status-radio-item-completed"
                      className="ps-4"
                      unit="item"
                      label="Completed"
                      name="status"
                      value="Completed"
                      disabled={
                        !(modalAction === 'EDIT' || modalAction === 'VIEW')
                          ? true
                          : undefined
                      }
                    />
                    <NeuRadio
                      data-testid="stoplight-modal-status-radio-item-in-progress"
                      className="ps-4"
                      unit="item"
                      label="In Progress"
                      name="status"
                      value="In Progress"
                    />
                    <NeuRadio
                      data-testid="stoplight-modal-status-radio-item-deferred"
                      className="ps-4"
                      unit="item"
                      label="Deferred"
                      name="status"
                      value="Deferred"
                    />
                  </>
                ) : (
                  <LabelContentBold>
                    {stoplightObject?.status ?? ''}
                  </LabelContentBold>
                )}
              </NeuRadio>
            </RadioContainer>

            {(modalAction === 'VIEW' || stoplight?.status === 'Completed') && (
              <>
                {stoplightObject?.origin === 'patient' && (
                  <>
                    <InputHeading>Unit</InputHeading>
                    <LabelContentBold>
                      {stoplightObject?.locations?.unit[0]?.displayName ||
                        'Unknown'}
                    </LabelContentBold>
                  </>
                )}

                <InputHeading>Stoplight Category</InputHeading>
                <LabelContentBold>{stoplightObject?.category}</LabelContentBold>
                {stoplightObject?.subCategory && (
                  <>
                    <InputHeading>Stoplight Subcategory</InputHeading>
                    <LabelContentBold>
                      {stoplightObject?.subCategory
                        ? stoplightObject?.subCategory
                        : 'Unknown'}
                    </LabelContentBold>
                  </>
                )}
                <InputHeading>Details</InputHeading>
                <LabelContentBold>{stoplightObject?.details}</LabelContentBold>
              </>
            )}

            {stoplightObject.origin === 'patient' &&
              modalAction !== 'VIEW' &&
              stoplight?.status !== 'Completed' && (
                <>
                  <InputHeading>Unit</InputHeading>
                  <Dropdown
                    id="Stoplight-Modal-Unit-Select"
                    classes="px-0"
                    name="unit"
                    open={openUnit}
                    placeholder={
                      units.find(
                        unit =>
                          unit.unitId === stoplightObject.locations.unit[0]?.id
                      )?.unit || 'Select a Unit'
                    }
                    setOpen={setOpenUnit}
                  >
                    {filteredUnits &&
                      filteredUnits.map((unit: Unit) => (
                        <div
                          aria-selected={
                            unit.unitId ===
                            stoplightObject.locations.unit[0]?.id
                          }
                          key={unit.unitId}
                          className={`dropdown-hover-item${
                            unit.unitId ===
                            stoplightObject.locations.unit[0]?.id
                              ? ' selected'
                              : ''
                          }`}
                          role="option"
                          tabIndex={0}
                          onClick={() => handleDropdownChange(unit, 'unit')}
                          onKeyDown={() => handleDropdownChange(unit, 'unit')}
                        >
                          {unit.unit}
                        </div>
                      ))}
                  </Dropdown>
                </>
              )}
            {modalAction !== 'VIEW' && stoplight?.status !== 'Completed' && (
              <>
                <InputHeading>Stoplight Category</InputHeading>
                <Dropdown
                  id="Stoplight-Modal-Category-Select"
                  classes="px-0"
                  name="category"
                  open={openCat}
                  placeholder={
                    stoplightObject.category
                      ? stoplightObject.category
                      : 'Select a Category'
                  }
                  setOpen={setOpenCat}
                >
                  {categories.map((category: Category) => (
                    <div
                      key={category.name}
                      aria-selected={category.name === stoplightObject.category}
                      className={`dropdown-hover-item${
                        category.name === stoplightObject.category
                          ? ' selected'
                          : ''
                      }`}
                      role="option"
                      tabIndex={0}
                      onClick={() =>
                        handleDropdownChange(category.name, 'category')
                      }
                      onKeyDown={() =>
                        handleDropdownChange(category.name, 'category')
                      }
                    >
                      {category.name}
                    </div>
                  ))}
                </Dropdown>
                <InputHeading>Stoplight Subcategory</InputHeading>
                <Dropdown
                  id="Stoplight-Modal-SubCategory-Select"
                  classes="px-0"
                  disabled={
                    !stoplightObject.category ||
                    (categories.length > 0 && subCategories.length < 1)
                  }
                  name="subCategory"
                  open={openSubcat}
                  placeholder={
                    stoplightObject.subCategory
                      ? stoplightObject.subCategory
                      : stoplightObject.category && subCategories.length === 0
                      ? 'No subcategories'
                      : 'Select a Subcategory'
                  }
                  setOpen={setOpenSubcat}
                >
                  {subCategories.map((subcat: string) => (
                    <div
                      key={subcat}
                      aria-selected={subcat === stoplightObject.subCategory}
                      className={`dropdown-hover-item${
                        subcat === stoplightObject.subCategory
                          ? ' selected'
                          : ''
                      }`}
                      role="option"
                      tabIndex={0}
                      onClick={() =>
                        handleDropdownChange(subcat, 'subCategory')
                      }
                      onKeyDown={() =>
                        handleDropdownChange(subcat, 'subCategory')
                      }
                    >
                      {subcat}
                    </div>
                  ))}
                </Dropdown>
                {(stoplightObject.category === 'ITG' ||
                  stoplightObject.category.includes('Maintenance/Access') ||
                  stoplightObject.category === 'GE Biomed' ||
                  stoplightObject.category.includes(
                    'Building Maintenance'
                  )) && (
                  <StyledBoldLabel>
                    Create an issue in addition to this Stoplight if a fix agent
                    is required.
                  </StyledBoldLabel>
                )}
                <ModalTextArea
                  id="Stoplight-Modal-Details-Text-Area"
                  inputMode="text"
                  label="Details (Do not include PHI)"
                  name="details"
                  required
                  spellCheck
                  value={stoplightObject.details ? stoplightObject.details : ''}
                  onNeuChange={handleInputs}
                />
              </>
            )}

            <div className="mb-4" id="Stoplight-Modal-Share-With">
              <InputHeading>
                {stoplight?.status !== 'Completed'
                  ? 'Share With (Optional)'
                  : 'Shared With'}
              </InputHeading>
              {sharedWithDisplay.length > 0 && (
                <div className="pt-1 pb-2">
                  {sharedWithDisplay.map((emp: string) => {
                    return (
                      <ChipWrapper
                        key={emp}
                        onClick={() => handleRemoveSharedWith(emp)}
                      >
                        <BlueChip key={emp} name={emp} />
                      </ChipWrapper>
                    );
                  })}
                </div>
              )}
              {stoplight?.status !== 'Completed' && (
                <div className="position-relative">
                  <Dropdown
                    id="Stoplight-Modal-Employee-Dropdown"
                    name="sharedWith"
                    classes="ps-0"
                    open={sharedPopState}
                    focused={activeSearch === 'sharedWith'}
                    placeholder="Start typing name or 3-4 ID"
                    query={sharedSearchTerm}
                    setOpen={setSharedPopState}
                    typeAhead
                    setTypeAhead={setActiveSearch}
                    setTypeAheadChange={handleSharedSearching}
                  >
                    {[...(employeesBySearchData ?? ([] as Employee[]))]
                      .sort(compareObjByStringKey('lastName'))
                      .sort(compareObjByStringKey('firstName'))
                      .map((emp: Employee) => (
                        <div key={emp.employeeId}>
                          <div
                            className={`dropdown-hover-item${
                              stoplightObject.sharedWith.includes(
                                emp.employeeId?.toUpperCase()
                              )
                                ? ' selected'
                                : ''
                            }`}
                            aria-selected={stoplightObject.sharedWith.includes(
                              emp.employeeId
                            )}
                            role="option"
                            tabIndex={0}
                            onMouseDown={() => {
                              if (emp.email) {
                                setEmployeeToShare(emp);
                                setSharedPopState(false);
                                setActiveSearch('');
                                setSharedSearchTerm('');
                                setSharedWithNoEmail(false);
                              } else {
                                setSharedWithNoEmail(true);
                              }
                            }}
                            onKeyDown={() => {
                              if (emp.email) {
                                setEmployeeToShare(emp);
                                setSharedPopState(false);
                                setActiveSearch('');
                                setSharedSearchTerm('');
                                setSharedWithNoEmail(false);
                              } else {
                                setSharedWithNoEmail(true);
                              }
                            }}
                          >
                            <div className="d-flex justify-content-between">
                              <p className="mt-1 mb-0">
                                {emp.firstName.toUpperCase()}{' '}
                                {emp.lastName.toUpperCase()}
                              </p>
                              <p
                                className="mt-1 mb-0"
                                // TODO: How to better handle this thru a class
                                style={{ fontSize: '14px' }}
                              >
                                {emp.employeeId}
                              </p>
                            </div>
                            <p
                              className="mt-1 mb-0"
                              style={{ fontSize: '14px' }}
                            >
                              {emp.title}
                            </p>
                            <p
                              className="mt-1 mb-0"
                              style={{ fontSize: '14px' }}
                            >
                              {emp.email}
                            </p>
                          </div>
                          {employeesBySearchData?.length > 1 && (
                            <StyledDivider className="my-0" />
                          )}
                        </div>
                      ))}
                  </Dropdown>
                  {sharedWithNoEmail && (
                    <StyledBoldLabel className="mt-2">
                      This employee cannot be shared with as they do not have an
                      email address in the system.
                    </StyledBoldLabel>
                  )}
                </div>
              )}
            </div>

            <ViewIssuesClickableLabel onClick={() => setView('search')}>
              {stoplightObject.issueIds?.length > 0 ||
              (stoplight?.status === 'Completed' &&
                stoplightObject.issueIds?.length === 0)
                ? 'Attached Issues'
                : 'Attach an Issue'}
            </ViewIssuesClickableLabel>

            {/* ***************COMMENTS**************** */}
            {(modalAction === 'EDIT' || modalAction === 'VIEW') && (
              <>
                <StyledDivider className="my-4" />
                {stoplightObject?.discussion?.comments.map((comment, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <StyledEditHeaderRow key={comment.date + i}>
                    <div className="px-1">
                      <NeuAvatar
                        color="blue"
                        initials={text.getInitials(comment.authorFullName)}
                      />
                    </div>
                    <div>
                      <StyledEditHeaderRow className="ps-0">
                        <StyledBoldHeading
                          className="neu-text-blue-60 mt-0"
                          autoWidth
                        >
                          {comment.authorFullName}
                        </StyledBoldHeading>
                        <InputHeading
                          className="mt-0 ms-1"
                          autoWidth
                          style={{ fontSize: '0.75rem' }}
                        >
                          {format(
                            date.parseStopDate(comment.date),
                            'MM/dd/yyyy'
                          )}
                        </InputHeading>
                      </StyledEditHeaderRow>
                      <StyledEditHeaderRow className="ps-0">
                        <InputHeading className="mt-0">
                          {comment.body}
                        </InputHeading>
                      </StyledEditHeaderRow>
                    </div>
                  </StyledEditHeaderRow>
                ))}

                {/* **************COMMENT TEXT AREA*************** */}
                {stoplight?.status !== 'Completed' && (
                  <StyledEditHeaderCol className="mt-3 ms-0 me-1">
                    <ModalTextArea
                      className="me-3 mb-2"
                      inputMode="text"
                      label="Add Comment (Do not include PHI)"
                      name="comment"
                      required
                      spellCheck
                      value={commentObj.comment.body}
                      onNeuChange={handleCommentText}
                    />
                    <StyledRightAlignButton
                      className="me-3"
                      color="blue"
                      size="small"
                      onClick={handleCommentPost}
                    >
                      Add Comment
                    </StyledRightAlignButton>
                  </StyledEditHeaderCol>
                )}
              </>
            )}
          </ModalCardContent>
        ))}
      {modalData && view === 'search' && (
        // Issue info
        <ModalCardContent
          id="Stoplight-Modal-Issues-Card"
          className="pb-4"
          unit="content"
        >
          <SearchView
            attachedDisplay={attachIssueDisplay as (Issue & Stoplight)[]}
            attachToItem={stoplightObject as TaskPost & Stoplight}
            attachToItemCompleted={stoplight?.status === 'Completed'}
            prevAttached={stoplightObject?.issueIds || []}
            type="issue"
            handleRemoveAttached={handleRemoveAttachIssue}
            setItemsToAttach={setIssuesToAttach}
          />
        </ModalCardContent>
      )}
      {!loading && (
        <ModalFooter
          unit="footer"
          right={
            !(
              stoplightObject &&
              (stoplightObject.authorId === authorizedUser.hcaid ||
                stoplightObject.completedDate)
            )
          }
        >
          {stoplight?.status === 'Completed' ? (
            <div className="d-flex align-items-center justify-content-start">
              <StyledCircleDoneIcon color="plain-0">done</StyledCircleDoneIcon>
              <div className="ms-2">
                <div className="d-flex ps-0 align-items-start">
                  <p className="my-0 bold">Completed</p>
                </div>
                {stoplightObject.completedDate && (
                  <div className="d-flex ps-0 align-items-start">
                    <p
                      className="my-0 w-auto custom-modal-heading"
                      style={{ fontSize: '0.75rem' }}
                    >
                      {date.dayOfYr(stoplightObject.completedDate)} @{' '}
                      {date.localTimeOfDay(stoplightObject.completedDate)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              {modalAction === 'EDIT' &&
                stoplightObject &&
                stoplightObject.authorId === authorizedUser.hcaid && (
                  <NeuButton
                    data-testid="stoplight-modal-delete-button"
                    color="red"
                    size="small"
                    onClick={handleDelete}
                  >
                    Delete
                  </NeuButton>
                )}
              <NeuButton
                data-testid="stoplight-modal-save-button"
                color="blue"
                disabled={!showButton ? true : undefined}
                size="small"
                style={{ float: 'right' }}
                onClick={handlePost}
              >
                Save Stoplight
              </NeuButton>
            </>
          )}
        </ModalFooter>
      )}
    </StoplightCard>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser.authorizedUser,
    categories: state.TaskReducer.categories,
    facilities: state.ConfigReducer.userAccessibleFacilities,
    globalUserSection: state.UserReducer.userSection,
    loading: state.StoplightReducer.loadingStoplight,
    modalState: state.ModalReducer.modal,
    modalAction: state.ModalReducer.modalAction,
    modalData: state.ModalReducer.modalData,
    stoplight: state.StoplightReducer.stoplight,
    tempTaskObject: state.TaskReducer.tempTaskObject,
    tempModalState: state.TaskReducer.tempModalState,
    units: state.ConfigReducer.units
  };
};

export default connect(mapReduxStateToProps)(StoplightModal);
